import {
  defineComponent,
  computed,
  onMounted,
  ref,
} from "@vue/composition-api";
import { itemRouteBuilder, NamedRts } from "@/router";

import { Editor, EditorContent } from "tiptap";
import TipTapDisplay from "@/components/TipTapDisplay/TipTapDisplay.vue";
import { Placeholder } from "tiptap-extensions";
import { BLANK_EDITOR_DOCUMENT } from "@/constants";

import { buildSubmitButton } from "@/utilities/form-helpers";
import { friendlyTimeStamp } from "@/utilities/display-helpers";

import { sellContractNotesController } from "@/controllers/sellContractNotes";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";

import { JSConn } from "@/utilities/js-conn";

import { SellContract } from "@/models/SellContract";
import { WeightQuantityPriceStatus } from "@/models/WeightQuantityPriceStatus";
import CircularPercentageChart from "@/components/CircularPercentageChart/CircularPercentageChart.vue";
import BarPercentageChart from "@/components/BarPercentageChart/BarPercentageChart.vue";

export default defineComponent({
  name: "sellContractDetails",
  props: {
    sellContractId: {
      type: String,
      required: true,
    },
  },
  components: {
    CircularPercentageChart,
    BarPercentageChart,
    LoadingAnimation,
    EditorContent,
    TipTapDisplay,
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    let deletingNoteId = ref("");

    let noteContentJSON = ref(BLANK_EDITOR_DOCUMENT);

    let editor = ref<any>(null);

    let { submitButtonText, submitting } = buildSubmitButton();
    submitButtonText.value = "Submit Note";

    const localLoadNotes = () => {
      const jsConnNotes = new JSConn();
      jsConnNotes.formParams = {
        sell_contract_id: props.sellContractId,
      };

      return sellContractNotesController
        .needSellContractNotes(jsConnNotes)
        .then(jsConn => {
          localNotes.value = jsConn.response;
          return Promise.resolve();
        });
    };

    const localData = () => {
      if (
        localSellContract.value.weight_status ===
        WeightQuantityPriceStatus.actual
      ) {
        return {
          lotUnitLabel: localSellContract.value.weight_lot_unit_label,

          endItemLabel: "End Weight",
          endAmount: localSellContract.value.end_weight,

          soldItemLabel: "Sold Weight",
          soldAmount: localSellContract.value.sold_weight,

          remainingLabel: "Remaining Weight",
          remainingAmount: localSellContract.value.remaining_weight,
        };
      } else {
        return {
          lotUnitLabel: localSellContract.value.lot_unit_label,

          endItemLabel: "End Quantity",
          endAmount: localSellContract.value.end_quantity,

          soldItemLabel: "Sold Quantity",
          soldAmount: localSellContract.value.sold_quantity,

          remainingLabel: "Remaining Quantity",
          remainingAmount: localSellContract.value.remaining_quantity,
        };
      }
    };

    const localEndQuantity = () => {
      if (
        localSellContract.value.weight_status ===
        WeightQuantityPriceStatus.actual
      ) {
        return localSellContract.value.end_weight;
      } else {
        return localSellContract.value.end_quantity;
      }
    };

    const localEndQuantityLabel = () => {
      if (
        localSellContract.value.weight_status ===
        WeightQuantityPriceStatus.actual
      ) {
        return localSellContract.value.weight_lot_unit_label;
      } else {
        return localSellContract.value.lot_unit_label;
      }
    };

    const deletingNote = (n: { id: string }) => {
      return n.id === deletingNoteId.value;
    };

    const deleteNote = (nt: { id: string }) => {
      deletingNoteId.value = nt.id;
      const jsConn = new JSConn();
      jsConn.formParams = {
        id: nt.id,
      };

      Promise.all([
        sellContractNotesController.deleteSellContractNote(jsConn),
      ]).then(() => {
        deletingNoteId.value = "";
        localLoadNotes();
      });
    };

    const submitNote = (evt: any) => {
      evt.preventDefault();
      submitting.value = true;
      submitButtonText.value = "Submitting...";

      const jsConn = new JSConn();
      const rightNow = new Date();
      noteContentJSON.value = editor.value.getJSON();

      jsConn.formParams = {
        content_json: noteContentJSON.value,
        sell_contract_id: props.sellContractId,
        created_at: rightNow,
      };

      sellContractNotesController.createSellContractNote(jsConn).then(() => {
        editor.value.setContent(BLANK_EDITOR_DOCUMENT);
        ctx.root.$nextTick(() => {
          editor.value.focus();
        });

        return localLoadNotes().then(_jsConn => {
          submitting.value = false;
          submitButtonText.value = "Submit Note";
        });
      });

      noteContentJSON.value = BLANK_EDITOR_DOCUMENT;
    };

    const localNotes = ref<any[]>([]);

    const localSellContract = computed(() => {
      return $store.getters.sellContract as SellContract;
    });

    onMounted(() => {
      localLoadNotes();

      editor.value = new Editor({
        extensions: [
          new Placeholder({
            emptyEditorClass: "is-empty",
            emptyNodeText: "Write a note...",
            showOnlyWhenEditable: true,
          }),
        ],
      });

      editor.value.setContent(BLANK_EDITOR_DOCUMENT);
    });

    const editRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.sellContractEditForm, id);
    };

    return {
      localSellContract,

      editRouteBuilder,

      editor,
      submitNote,
      submitting,
      submitButtonText,

      localNotes,
      friendlyTimeStamp,
      deleteNote,
      deletingNote,

      localEndQuantity,
      localEndQuantityLabel,
      localData,
    };
  },
});
